import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PushGatewayCapabilities as PushGatewayCapabilitiesDto,
  PushGatewayDeliveriesResult as PushGatewayDeliveriesResultDto,
  PushGatewayDeliveryStatus as PushGatewayDeliveryStatusDto,
  PushGatewayRegistration as PushGatewayRegistrationDto,
  PushGatewayStatus as PushGatewayStatusDto,
  PushGatewayTestPushResult as PushGatewayTestPushResultDto,
} from '@sbclite/core/push-gateway';
import { api } from '../services/api';

export const pushGatewayQueryKeys = {
  all: ['push-gateway'] as const,
  capabilities: () => [...pushGatewayQueryKeys.all, 'capabilities'] as const,
  status: () => [...pushGatewayQueryKeys.all, 'status'] as const,
  registrations: (filters: PushGatewayRegistrationFilters) => [...pushGatewayQueryKeys.all, 'registrations', filters] as const,
  deliveries: (filters: PushGatewayDeliveryFilters) => [...pushGatewayQueryKeys.all, 'deliveries', filters] as const,
};

export type { PushGatewayCapabilities, PushGatewayDelivery, PushGatewayDeliveryStatus, PushGatewayRegistration, PushGatewayStatus, PushGatewayTestPushResult } from '@sbclite/core/push-gateway';

export interface PushGatewayRegistrationFilters {
  limit: number;
  offset: number;
  activeOnly: boolean;
  domain?: string;
}

export interface PushGatewayDeliveryFilters {
  limit: number;
  aor?: string;
  callId?: string;
  status?: PushGatewayDeliveryStatusDto;
  cursor?: string;
}

interface Pagination {
  limit: number;
  offset: number;
  total: number;
  totalPages: number;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

interface PaginatedApiEnvelope<T> extends ApiEnvelope<T[]> {
  pagination: Pagination;
}

type PushGatewayStatusResponse = ApiEnvelope<PushGatewayStatusDto>;

type PushGatewayCapabilitiesResponse = ApiEnvelope<PushGatewayCapabilitiesDto>;

type PushGatewayRegistrationsResponse = PaginatedApiEnvelope<PushGatewayRegistrationDto>;

type RevokePushBindingsResponse = ApiEnvelope<{
  aor: string;
  revoked: boolean;
  revokedCount: number;
}>;

type SendTestPushResponse = ApiEnvelope<PushGatewayTestPushResultDto>;

type PushGatewayDeliveriesResponse = ApiEnvelope<PushGatewayDeliveriesResultDto>;

export type CleanupExpiredRegistrationsResponse = ApiEnvelope<{
  deletedCount: number;
  cleanedAt: string;
}>;

export function usePushGatewayCapabilities() {
  return useQuery({
    queryKey: pushGatewayQueryKeys.capabilities(),
    queryFn: async () => {
      const response = await api.get<PushGatewayCapabilitiesResponse>('/api/push-gateway/capabilities');
      return response.data;
    },
  });
}

export function usePushGatewayStatus(refetchInterval: number | false, enabled = true) {
  return useQuery({
    queryKey: pushGatewayQueryKeys.status(),
    queryFn: async () => {
      const response = await api.get<PushGatewayStatusResponse>('/api/push-gateway/status');
      return response.data;
    },
    refetchInterval,
    enabled,
  });
}

export function usePushGatewayRegistrations(filters: PushGatewayRegistrationFilters, refetchInterval: number | false, enabled = true) {
  return useQuery({
    queryKey: pushGatewayQueryKeys.registrations(filters),
    queryFn: async () => {
      const response = await api.get<PushGatewayRegistrationsResponse>('/api/push-gateway/registrations', {
        limit: filters.limit,
        offset: filters.offset,
        activeOnly: filters.activeOnly,
        domain: filters.domain,
      });
      return response.data;
    },
    refetchInterval,
    enabled,
  });
}

export function usePushGatewayDeliveries(filters: PushGatewayDeliveryFilters, refetchInterval: number | false, enabled = true) {
  return useQuery({
    queryKey: pushGatewayQueryKeys.deliveries(filters),
    queryFn: async () => {
      const response = await api.get<PushGatewayDeliveriesResponse>('/api/push-gateway/deliveries', {
        limit: filters.limit,
        aor: filters.aor,
        callId: filters.callId,
        status: filters.status,
        cursor: filters.cursor,
      });
      return response.data;
    },
    refetchInterval,
    enabled,
  });
}

export function useRevokePushBindings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (aor: string) => {
      const response = await api.post<RevokePushBindingsResponse>('/api/push-gateway/bindings/revoke', { aor });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pushGatewayQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sip-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['sip-registration-statistics'] });
    },
  });
}

export function useSendTestPush() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (aor: string) => {
      const response = await api.post<SendTestPushResponse>('/api/push-gateway/test', { aor });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pushGatewayQueryKeys.all });
    },
  });
}

export function useCleanupExpiredSipRegistrations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<CleanupExpiredRegistrationsResponse>('/api/sip-registrations/cleanup');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pushGatewayQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sip-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['sip-registration-statistics'] });
    },
  });
}
